import React, { useCallback, useEffect, useMemo, useState } from "react";
import { canShowTeamChat, getUserId } from "../../utils/auth";
import { useAppFeatures } from "../../hooks/useAppFeatures";
import { useTeamChatAccess } from "../../hooks/useTeamChatAccess";
import {
    createConversation,
    fetchContacts,
    fetchConversations,
    fetchMessages,
    fetchUnreadCount,
    markConversationRead,
    chatUserFacingMessage,
    type ChatContact,
    type ChatMessage,
    type ConversationSummary,
} from "../../services/teamChatService";
import { useTeamChatSocket } from "./useTeamChatSocket";
import "../../styles/team-chat.css";

type PanelView = "inbox" | "contacts" | "thread";

function formatTime(iso: string | null): string {
    if (!iso) {
        return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatRole(role: string | undefined): string {
    if (!role) {
        return "";
    }
    return role
        .toLowerCase()
        .split(/[_\s]+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return "?";
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function ChatAvatar({ name, imageUrl }: { name: string; imageUrl?: string | null }) {
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [imageUrl]);

    const showPhoto = Boolean(imageUrl && imageUrl.trim() && !failed);

    return (
        <span className="team-chat-avatar" aria-hidden="true">
            {showPhoto ? (
                <img src={imageUrl as string} alt="" onError={() => setFailed(true)} />
            ) : (
                initials(name)
            )}
        </span>
    );
}

function ChatBubbleIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
        </svg>
    );
}

function BackIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
        </svg>
    );
}

function PlusIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
        </svg>
    );
}

function SendIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 2 11 13" />
            <path d="M22 2 15 22l-4-9-9-4 20-7z" />
        </svg>
    );
}

export default function TeamChatOverlay() {
    const roleVisible = canShowTeamChat();
    const { features, loading: featuresLoading } = useAppFeatures();
    const featureEnabled = !featuresLoading && features?.instantChatEnabled === true;
    const { visible: accessVisible } = useTeamChatAccess();
    const visible = roleVisible && featureEnabled && accessVisible;
    const [open, setOpen] = useState(false);
    const [view, setView] = useState<PanelView>("inbox");
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [contacts, setContacts] = useState<ChatContact[]>([]);
    const [contactQuery, setContactQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [active, setActive] = useState<ConversationSummary | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [unreadIds, setUnreadIds] = useState<Set<number>>(new Set());

    const { connected, socketError, incoming, presence, send, clearSocketError } = useTeamChatSocket(visible);
    const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());
    const parsedUserId = Number(getUserId());
    const currentUserId = parsedUserId > 0 ? parsedUserId : NaN;

    const loadInbox = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const page = await fetchConversations();
            const rows = page.content || [];
            setConversations(rows);
            setUnreadIds(new Set(rows.filter((row) => (row.unreadCount ?? 0) > 0).map((row) => row.id)));
            setOnlineUserIds((prev) => {
                const next = new Set(prev);
                rows.forEach((row) => {
                    const userId = row.otherParticipant?.userId;
                    if (userId == null) {
                        return;
                    }
                    if (row.otherParticipant?.online) {
                        next.add(userId);
                    } else {
                        next.delete(userId);
                    }
                });
                return next;
            });
        } catch (err) {
            setError(chatUserFacingMessage(
                err instanceof Error ? err.message : undefined,
                "Unable to load conversations.",
            ));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadContacts = useCallback(async (query: string) => {
        setLoading(true);
        setError(null);
        try {
            const page = await fetchContacts(query);
            setContacts(page.content || []);
        } catch (err) {
            setError(chatUserFacingMessage(
                err instanceof Error ? err.message : undefined,
                "Unable to load people.",
            ));
        } finally {
            setLoading(false);
        }
    }, []);

    const dismissUnread = useCallback((conversationId: number) => {
        setUnreadIds((prev) => {
            if (!prev.has(conversationId)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(conversationId);
            return next;
        });
    }, []);

    const clearUnread = useCallback((conversationId: number) => {
        dismissUnread(conversationId);
        void markConversationRead(conversationId);
    }, [dismissUnread]);

    const openThread = useCallback(async (conversation: ConversationSummary) => {
        setActive(conversation);
        setView("thread");
        setLoading(true);
        setError(null);
        setMessages([]);
        dismissUnread(conversation.id);
        try {
            const history = await fetchMessages(conversation.id);
            setMessages(history);
        } catch (err) {
            setError(chatUserFacingMessage(
                err instanceof Error ? err.message : undefined,
                "Unable to load messages.",
            ));
        } finally {
            setLoading(false);
        }
    }, [dismissUnread]);

    useEffect(() => {
        const handle = window.setTimeout(() => setDebouncedQuery(contactQuery), 300);
        return () => window.clearTimeout(handle);
    }, [contactQuery]);

    useEffect(() => {
        if (!visible) {
            return;
        }
        let cancelled = false;
        void fetchUnreadCount()
            .then((summary) => {
                if (!cancelled) {
                    setUnreadIds((prev) => {
                        const next = new Set(summary.conversationIds || []);
                        prev.forEach((id) => next.add(id));
                        return next;
                    });
                }
            })
            .catch(() => {
                // Overlay remains usable if unread state cannot be loaded.
            });
        return () => {
            cancelled = true;
        };
    }, [visible]);

    useEffect(() => {
        if (!open || !visible) {
            return;
        }
        if (view === "inbox") {
            void loadInbox();
        }
        if (view === "contacts") {
            void loadContacts(debouncedQuery);
        }
    }, [open, visible, view, debouncedQuery, loadInbox, loadContacts]);

    useEffect(() => {
        if (!incoming) {
            return;
        }
        const fromSelf = Number.isFinite(currentUserId) && incoming.senderId === currentUserId;
        const viewingThread = open && view === "thread" && active?.id === incoming.conversationId;
        if (!fromSelf && viewingThread) {
            clearUnread(incoming.conversationId);
        } else if (!fromSelf) {
            setUnreadIds((prev) => {
                if (prev.has(incoming.conversationId)) {
                    return prev;
                }
                const next = new Set(prev);
                next.add(incoming.conversationId);
                return next;
            });
        }
        setConversations((prev) => {
            const match = prev.find((c) => c.id === incoming.conversationId);
            const preview = incoming.body;
            if (!match) {
                void loadInbox();
                return prev;
            }
            const updated: ConversationSummary = {
                ...match,
                lastMessageAt: incoming.createdAt,
                lastMessagePreview: preview,
                unreadCount: fromSelf || viewingThread ? 0 : 1,
            };
            return [updated, ...prev.filter((c) => c.id !== incoming.conversationId)];
        });
        setMessages((prev) => {
            if (!active || active.id !== incoming.conversationId) {
                return prev;
            }
            if (prev.some((m) => m.id === incoming.id)) {
                return prev;
            }
            return [...prev, incoming];
        });
    }, [incoming, active, loadInbox, open, view, currentUserId, clearUnread]);

    useEffect(() => {
        if (!presence || !Number.isFinite(presence.userId)) {
            return;
        }
        setOnlineUserIds((prev) => {
            const already = prev.has(presence.userId);
            if (presence.online === already) {
                return prev;
            }
            const next = new Set(prev);
            if (presence.online) {
                next.add(presence.userId);
            } else {
                next.delete(presence.userId);
            }
            return next;
        });
    }, [presence]);

    const handleToggle = () => {
        setOpen((wasOpen) => {
            if (wasOpen) {
                setView("inbox");
                setActive(null);
                setDraft("");
                setError(null);
                clearSocketError();
            }
            return !wasOpen;
        });
    };

    const handleSelectContact = async (contact: ChatContact) => {
        setLoading(true);
        setError(null);
        try {
            const conversation = await createConversation(contact.userId);
            await openThread(conversation);
            await loadInbox();
        } catch (err) {
            setError(chatUserFacingMessage(
                err instanceof Error ? err.message : undefined,
                "Unable to start that conversation.",
            ));
        } finally {
            setLoading(false);
        }
    };

    const handleSend = (event: React.FormEvent) => {
        event.preventDefault();
        if (!active || !draft.trim()) {
            return;
        }
        const sent = send(active.id, draft.trim());
        if (sent) {
            setDraft("");
            clearSocketError();
            dismissUnread(active.id);
        }
    };

    const handleDraftKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
        }
    };

    const panelTitle = useMemo(() => {
        if (view === "contacts") {
            return "New message";
        }
        if (view === "thread") {
            return active?.otherParticipant?.displayName || "Conversation";
        }
        return "Messages";
    }, [view, active]);

    const peerUserId = active?.otherParticipant?.userId;
    const peerOnline = peerUserId != null && onlineUserIds.has(peerUserId);
    const statusLabel = !connected
        ? "Reconnecting…"
        : view === "thread" && peerOnline
            ? "Live"
            : null;

    const unreadTotal = unreadIds.size;
    const fabLabel = open
        ? "Close messages"
        : unreadTotal > 0
            ? `Open messages, ${unreadTotal} unread`
            : "Open messages";

    if (!visible) {
        return null;
    }

    return (
        <div className="team-chat-root">
            {open && (
                <section className="team-chat-panel" aria-label="Messages">
                    <header className="team-chat-header">
                        {view !== "inbox" && (
                            <button
                                type="button"
                                className="team-chat-icon-btn team-chat-back"
                                aria-label="Back"
                                onClick={() => {
                                    setView("inbox");
                                    setActive(null);
                                    setMessages([]);
                                    setError(null);
                                }}
                            >
                                <BackIcon />
                            </button>
                        )}
                        <div className="team-chat-header-copy">
                            <div className="team-chat-title-row">
                                <h2 className="team-chat-title">{panelTitle}</h2>
                                {statusLabel && (
                                    <p className={`team-chat-status${connected ? "" : " is-offline"}`} aria-live="polite">
                                        {statusLabel}
                                    </p>
                                )}
                            </div>
                            {view === "thread" && active?.otherParticipant?.role && (
                                <p className="team-chat-header-role">{formatRole(active.otherParticipant.role)}</p>
                            )}
                        </div>
                        <button type="button" className="team-chat-icon-btn team-chat-close" onClick={handleToggle} aria-label="Close chat panel">
                            <CloseIcon />
                        </button>
                    </header>
                    {(error || socketError) && (
                        <p className="team-chat-error" role="alert">
                            {chatUserFacingMessage(error || socketError || undefined, "Unable to load messages.")}
                        </p>
                    )}
                    {view === "inbox" && (
                        <div className="team-chat-body">
                            <button
                                type="button"
                                className="team-chat-new"
                                onClick={() => setView("contacts")}
                            >
                                <PlusIcon />
                                New message
                            </button>
                            {loading && <p className="team-chat-muted">Loading conversations…</p>}
                            {!loading && conversations.length === 0 && (
                                <div className="team-chat-empty">
                                    <span className="team-chat-empty-icon">
                                        <ChatBubbleIcon />
                                    </span>
                                    <p className="team-chat-muted">No conversations yet. Start one with someone at your school.</p>
                                </div>
                            )}
                            <ul className="team-chat-list">
                                {conversations.map((conversation) => {
                                    const name = conversation.otherParticipant?.displayName || "Conversation";
                                    const unread = unreadIds.has(conversation.id);
                                    return (
                                        <li key={conversation.id}>
                                            <button
                                                type="button"
                                                className={`team-chat-list-item${unread ? " is-unread" : ""}`}
                                                onClick={() => void openThread(conversation)}
                                            >
                                                <ChatAvatar name={name} imageUrl={conversation.otherParticipant?.avatarUrl} />
                                                <span className="team-chat-list-name">{name}</span>
                                                <span className="team-chat-list-time">{formatTime(conversation.lastMessageAt)}</span>
                                                <span className="team-chat-list-preview">
                                                    {conversation.lastMessagePreview || "No messages yet"}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                    {view === "contacts" && (
                        <div className="team-chat-body">
                            <label className="team-chat-search-label" htmlFor="team-chat-search">
                                Search people at your school
                            </label>
                            <input
                                id="team-chat-search"
                                className="team-chat-search"
                                type="search"
                                value={contactQuery}
                                onChange={(event) => setContactQuery(event.target.value)}
                                placeholder="Search by name"
                            />
                            {loading && <p className="team-chat-muted">Loading people…</p>}
                            {!loading && contacts.length === 0 && (
                                <div className="team-chat-empty">
                                    <p className="team-chat-muted">No matching administrators, staff, or coaches at your school.</p>
                                </div>
                            )}
                            <ul className="team-chat-list">
                                {contacts.map((contact) => (
                                    <li key={contact.userId}>
                                        <button
                                            type="button"
                                            className="team-chat-list-item"
                                            onClick={() => void handleSelectContact(contact)}
                                        >
                                            <ChatAvatar name={contact.displayName} imageUrl={contact.avatarUrl} />
                                            <span className="team-chat-list-name">{contact.displayName}</span>
                                            <span className="team-chat-list-preview">{formatRole(contact.role)}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {view === "thread" && active && (
                        <div className="team-chat-thread">
                            <div className="team-chat-messages" role="log" aria-live="polite">
                                {loading && <p className="team-chat-muted">Loading messages…</p>}
                                {!loading && messages.length === 0 && !error && (
                                    <div className="team-chat-empty">
                                        <span className="team-chat-empty-icon">
                                            <ChatBubbleIcon />
                                        </span>
                                        <p className="team-chat-muted">No messages yet. Say hello.</p>
                                    </div>
                                )}
                                {messages.map((message) => {
                                    const own = Number.isFinite(currentUserId) && message.senderId === currentUserId;
                                    return (
                                        <article
                                            key={message.id}
                                            className={`team-chat-bubble ${own ? "is-own" : "is-other"}`}
                                        >
                                            {!own && (
                                                <ChatAvatar
                                                    name={message.senderDisplayName}
                                                    imageUrl={active.otherParticipant?.avatarUrl}
                                                />
                                            )}
                                            <div className="team-chat-bubble-stack">
                                                <header className="team-chat-bubble-meta">
                                                    <span>{own ? "You" : message.senderDisplayName}</span>
                                                    <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
                                                </header>
                                                <p className="team-chat-bubble-body">{message.body}</p>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                            <form className="team-chat-composer" onSubmit={handleSend}>
                                <label htmlFor="team-chat-draft" className="visually-hidden">
                                    Message
                                </label>
                                <div className="team-chat-draft-wrap">
                                    <textarea
                                        id="team-chat-draft"
                                        className="team-chat-draft"
                                        rows={1}
                                        maxLength={4000}
                                        value={draft}
                                        onChange={(event) => setDraft(event.target.value)}
                                        onKeyDown={handleDraftKeyDown}
                                        placeholder="Write a message"
                                    />
                                </div>
                                <button type="submit" className="team-chat-send" disabled={!draft.trim() || !connected} aria-label="Send">
                                    <SendIcon />
                                    <span className="visually-hidden">Send</span>
                                </button>
                            </form>
                        </div>
                    )}
                </section>
            )}
            <button
                type="button"
                className="team-chat-fab"
                aria-label={fabLabel}
                aria-expanded={open}
                onClick={handleToggle}
            >
                {open ? <CloseIcon /> : <ChatBubbleIcon />}
                {!open && unreadTotal > 0 && (
                    <span className="team-chat-fab-badge" aria-hidden="true">
                        {unreadTotal > 9 ? "9+" : unreadTotal}
                    </span>
                )}
            </button>
        </div>
    );
}
